import * as React from 'react';
import * as Sentry from '@sentry/react';
import {
    CreateBase,
    useNotify,
    useRefresh,
} from 'react-admin';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton as MuiIconButton,
    Box,
    Button,
    FormControl,
    Select,
    FormHelperText,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { TextField as MuiTextField, MenuItem } from '@mui/material';
import CategoryService from '../../services/category/categoryService.ts';
import { useParams } from 'react-router-dom';
import { Language } from '../../enums/Language.ts';
import TranslateService from '../../services/translate/translateService.ts';

const LANGUAGE = Object.entries(Language).map(([id, name]) => ({
    id,
    name,
}));

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-secondary)' },
        '& .MuiSelect-outlined': {
            borderRadius: '6px',
            height: '1.4375em',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-secondary)' },
        },
    },
    '& .MuiInputBase-input': { fontSize: 14, padding: '10px 12px' },
};

type Props = { open: boolean; onClose: () => void };

export const CategoryAddDialog: React.FC<Props> = ({ open, onClose }) => {
    React.useEffect(() => {
        if (!open) {
            setTranslated(false);
            setTranslations([]);
            setIsTranslating(false);
        }
    }, [open]);
    const translateService = new TranslateService();
    const categoryService = new CategoryService();
    const [translated, setTranslated] = React.useState(false);
    const [translations, setTranslations] = React.useState<any[]>([]);
    const [isTranslating, setIsTranslating] = React.useState(false);
    const notify = useNotify();
    const refresh = useRefresh();
    const { id } = useParams();

    const initialValues = {
        name: '',
        language: '',
    };

    const validationSchema = Yup.object({
        name: Yup.string().required('Name is required'),
        language: Yup.string().required('Language is required')
    });

    const { t } = useTranslation();

    return (
        <CreateBase resource="commissionManagement">
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="lg"
                slotProps={{
                    backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.5)' } },
                    paper: { sx: { borderRadius: 2, border: '1px solid var(--color-secondary)' } },
                }}
                sx={{
                    padding: 0,
                    maxWidth: 700,
                    mx: 'auto',
                    width: '100%'
                }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {t('locationItems.dialog.addCategory')}
                    <MuiIconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </MuiIconButton>
                </DialogTitle>

                <DialogContent dividers>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={async () => {
                            try {
                                const englishTranslation = translations.find(
                                    tr => tr.language === "English"
                                );
                                const payload = {
                                    name: englishTranslation?.translatedName,
                                    locationId: id,
                                };
                                console.log(payload);
                                await categoryService.createCategory(payload);

                                notify(t('common.saved'), { type: 'success' });
                                refresh();
                                onClose();
                            } catch (error: any) {
                                notify(error.response.data.error, { type: 'error' });
                                console.error(error.response.data.error);
                                Sentry.captureException(error);
                            }
                        }}
                    >
                        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
                            <Form onSubmit={handleSubmit}>
                                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, width: '100%' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <FormControl
                                                size="small"
                                                fullWidth
                                                error={touched.language && Boolean(errors.language)}>
                                                <Select
                                                    name="language"
                                                    value={values.language}
                                                    onBlur={handleBlur}
                                                    onChange={(e) => {
                                                        handleChange(e);

                                                        const langId = e.target.value;
                                                        const langName = LANGUAGE.find(l => l.id === langId)?.name;

                                                        if (translated && langName) {
                                                            const match = translations.find(t => t.language === langName);

                                                            if (match) {
                                                                setFieldValue("name", match.translatedName);
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="" disabled>
                                                        {t('locationsManagement.create.language')}
                                                    </MenuItem>
                                                    {LANGUAGE.map(c => (
                                                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                                    ))}
                                                </Select>

                                                <FormHelperText>
                                                    {touched.language && errors.language ? errors.language : " "}
                                                </FormHelperText>
                                            </FormControl>
                                            <MuiTextField
                                                name="name"
                                                label=""
                                                placeholder={t('locationItems.dialog.name')}
                                                sx={inputSx}
                                                size="small"
                                                fullWidth
                                                value={values.name}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.name && Boolean(errors.name)}
                                                helperText={touched.name && errors.name}
                                            />
                                        </Box>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                                    <Button
                                        type="button"
                                        variant="contained"
                                        disabled={isTranslating || !values.name || !values.language || translated}
                                        sx={{
                                            borderRadius: 0,
                                            backgroundColor: 'var(--color-primary)',
                                            boxShadow: 'none',
                                            alignSelf: 'flex-end',
                                            width: '95px',
                                            '&:hover': { cursor: 'pointer', backgroundColor: 'var(--color-primary-hover)', boxShadow: 'var(--shadow-1)' },
                                        }}
                                        onClick={async () => {
                                            try {
                                                setIsTranslating(true);
                                                const payload = {
                                                    locationId: id,
                                                    categoryName: values.name,
                                                };
                                                const response = await translateService.translateCategory(payload);

                                                const sourceLangName = LANGUAGE.find(l => l.id === values.language)?.name;

                                                const fullTranslations = [
                                                    { language: sourceLangName, translatedName: values.name },
                                                    ...response
                                                ];
                                                setTranslations(fullTranslations);
                                                const langName = LANGUAGE.find(l => l.id === values.language)?.name;
                                                const match = fullTranslations.find(t => t.language === langName);

                                                if (match) {
                                                    setFieldValue("name", match.translatedName);
                                                }

                                                setIsTranslating(false);
                                                setTranslated(true);
                                                notify('Translation completed', { type: 'success' });
                                            } catch (error: any) {
                                                notify(error.response.data.error, { type: 'error' });
                                                console.error(error.response.data.error);
                                                Sentry.captureException(error);
                                                setIsTranslating(false);
                                            }

                                        }}
                                    >
                                        {isTranslating ? <CircularProgress size={25} sx={{ color: 'white' }} /> : t('locationsManagement.create.translate')}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        type="button"
                                        onClick={onClose}
                                        sx={{ borderRadius: 0, borderColor: 'var(--color-error)', color: 'var(--color-error)' }}
                                    >
                                        {t('common.cancel')}
                                    </Button>

                                    <Button
                                        type="submit"
                                        // disabled={translations.length === 0}
                                        sx={{
                                            borderRadius: 0,
                                            backgroundColor: 'var(--color-primary)',
                                            color: 'var(--color-text-light)',
                                            boxShadow: 'none',
                                            '&:hover': { cursor: 'pointer', backgroundColor: 'var(--color-primary-hover)', boxShadow: 'var(--shadow-1)' },
                                            '&:disabled': { cursor: 'default', backgroundColor: 'var(--color-button-background-disabled)', color: 'var(--color-button-text-disabled)' }
                                        }}
                                    >
                                        {t('common.saveChanges')}
                                    </Button>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>
        </CreateBase>
    );
};
